import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import PageHero, { HeroCtas } from '../components/ui/PageHero';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { SectionHead } from '../components/ui/SectionHead';
import DataTable from '../components/ui/DataTable';
import Notice from '../components/ui/Notice';
import StageList from '../components/ui/StageList';
import Faq from '../components/ui/Faq';
import Button from '../components/ui/Button';
import { serviceSchema, faqSchema, breadcrumbSchema } from '../components/ui/schema';

const InnovationVentures = () => {
  const { t } = useTranslation('cy');
  const meta = t('innovation.meta', { returnObjects: true });

  const faqItems = t('innovation.faq.items', { returnObjects: true });

  const schema = [
    serviceSchema('Innovation & Ventures', meta.description),
    faqSchema(faqItems),
    breadcrumbSchema([{ label: 'Home', to: '/' }, { label: 'Innovation & Ventures', to: '/innovation-ventures' }]),
  ];

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/innovation-ventures" schema={schema} />

      <PageHero
        eyebrow={t('innovation.hero.eyebrow')}
        title={t('innovation.hero.h1')}
        lead={t('innovation.hero.lead')}
        breadcrumb={<Breadcrumbs items={[{ label: t('nav.pillars.innovation.label') }]} />}
      >
        <p className="cy-body" style={{ marginTop: 24 }}>{t('innovation.hero.boundary')}</p>
        <HeroCtas
          primary={t('innovation.hero.cta')}
          primaryTo="/submit-opportunity?type=innovation"
          secondary={t('innovation.hero.seeCriteria')}
          secondaryTo="#innovation-themes"
        />
      </PageHero>

      {/* innovation-themes */}
      <section className="cy-section cy-section--alt" id="innovation-themes">
        <div className="cy-container">
          <SectionHead title={t('innovation.themes.h2')} lead={t('innovation.themes.lead')} />
          <DataTable
            columns={[
              { key: 'problem', label: 'Industry problem' },
              { key: 'tech', label: 'Example technologies' },
              { key: 'result', label: 'Measurable result' },
            ]}
            rows={t('innovation.themes.rows', { returnObjects: true })}
          />
          <p className="cy-body" style={{ marginTop: 24 }}>{t('innovation.themes.exclude')}</p>
        </div>
      </section>

      {/* innovation-readiness */}
      <section className="cy-section" id="innovation-readiness">
        <div className="cy-container">
          <SectionHead title={t('innovation.readiness.h2')} lead={t('innovation.readiness.lead')} />
          <DataTable
            columns={[
              { key: 'stage', label: 'Stage' },
              { key: 'evidence', label: 'Evidence expected' },
              { key: 'invest', label: 'Investment' },
              { key: 'pilot', label: 'Pilot / collaboration' },
            ]}
            rows={t('innovation.readiness.rows', { returnObjects: true })}
          />
          <Notice variant="inline" style={{ marginTop: 24 }}>{t('innovation.readiness.note')}</Notice>
        </div>
      </section>

      {/* innovation-validation */}
      <section className="cy-section cy-section--alt" id="innovation-validation">
        <div className="cy-container">
          <SectionHead eyebrow={t('innovation.validation.eyebrow')} title={t('innovation.validation.h2')} lead={t('innovation.validation.lead')} />
          <DataTable
            columns={[
              { key: 'dim', label: 'Dimension' },
              { key: 'what', label: 'What we look at' },
              { key: 'evidence', label: 'Evidence examples' },
            ]}
            rows={t('innovation.validation.rows', { returnObjects: true })}
          />
          <Notice variant="info" style={{ marginTop: 24 }}>{t('innovation.validation.ai')}</Notice>
        </div>
      </section>

      {/* innovation-pathways */}
      <section className="cy-section" id="innovation-pathways">
        <div className="cy-container">
          <SectionHead title={t('innovation.pathways.h2')} lead={t('innovation.pathways.lead')} />
          <DataTable
            columns={[
              { key: 'path', label: 'Path' },
              { key: 'precondition', label: 'Precondition' },
              { key: 'role', label: 'Cyberate role' },
              { key: 'notPromise', label: 'We do not promise' },
            ]}
            rows={t('innovation.pathways.rows', { returnObjects: true })}
          />
          <Notice variant="compliance" style={{ marginTop: 24 }}>{t('innovation.pathways.note')}</Notice>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/approach#approach-roles">{t('innovation.pathways.cta')}</Link>
          </p>
        </div>
      </section>

      {/* innovation-materials */}
      <section className="cy-section cy-section--alt" id="innovation-materials">
        <div className="cy-container">
          <SectionHead title={t('innovation.materials.h2')} lead={t('innovation.materials.lead')} />
          <div className="cy-grid cy-grid--2">
            <div className="cy-card">
              <h3 className="cy-card-title">Minimum</h3>
              <ul className="cy-checklist">
                {t('innovation.materials.minimum', { returnObjects: true }).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="cy-card">
              <h3 className="cy-card-title">Can follow later</h3>
              <ul className="cy-checklist">
                {t('innovation.materials.later', { returnObjects: true }).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <Notice variant="inline" style={{ marginTop: 24 }}>{t('innovation.materials.privacy')}</Notice>
          <div style={{ marginTop: 32 }}>
            <Button to="/submit-opportunity?type=innovation">{t('innovation.materials.cta')}</Button>
          </div>
        </div>
      </section>

      {/* innovation-process */}
      <section className="cy-section" id="innovation-process">
        <div className="cy-container">
          <SectionHead title={t('innovation.process.h2')} lead={t('innovation.process.lead')} />
          <StageList items={t('innovation.process.steps', { returnObjects: true })} />
          <p className="cy-fineprint" style={{ marginTop: 24 }}>{t('innovation.process.note')}</p>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/approach#approach-stages">{t('innovation.process.cta')}</Link>
          </p>
        </div>
      </section>

      {/* innovation-faq-cta */}
      <section className="cy-section cy-section--alt" id="innovation-faq-cta">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('innovation.faq.h2')} />
          <Faq items={faqItems} idPrefix="innovation-faq" />
          <div style={{ marginTop: 48, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
            <Button to="/submit-opportunity?type=innovation">{t('innovation.faq.cta')}</Button>
            <Link className="cy-link" to="/contact?topic=partnership">{t('innovation.faq.alt')}</Link>
          </div>
          <p className="cy-fineprint" style={{ marginTop: 32 }}>
            <Link className="cy-link" to="/property-development">Property Development</Link>
            {' · '}
            <Link className="cy-link" to="/built-environment-investments">Built Environment Investments</Link>
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default InnovationVentures;
